import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Collapse, Skeleton, Tooltip } from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { colors, typography } from '../../styles/theme';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { toast } from '../../Components/Toast';
import { BlockCollapseToggle, BlockHeader, PRIVATE_BLOCK_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { updateCollectionInquiry } from '../../services/collectionInquiryService';
import { getRecordLeads, isRecordLeadsHidden, readRecordLeads } from '../../services/leadsService';
import { isCollectionInquiryUnseen } from '../../Map/utils/collectionInquiryDisplay';
import {
  RECORD_LEADS_EMPTY_SUMMARY,
  RECORD_LEADS_TITLE,
  applyRecordInquirySeen,
  describeRecordLeadsStats,
  describeRecordUnseen,
} from '../recordLeads';
import { groupInquiriesByLead } from '../inquiryLeadGroups';
import InquiryCard from './InquiryCard';
import InquiryLeadGroup from './InquiryLeadGroup';
import { formatRelativeActivity } from '../commercialSummary';

const PAGE_SIZE = 20;
const skSx = { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '8px' };

/** Badge de no leídas: misma píldora primary que "Leads y consultas". */
function UnseenBadge({ count }) {
  const text = describeRecordUnseen(count);
  if (!text) return null;
  return (
    <Tooltip title={text} placement="top" arrow>
      <Box
        component="span"
        aria-label={text}
        data-testid="record-leads-unseen"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 20,
          minWidth: 20,
          px: 0.75,
          borderRadius: '999px',
          bgcolor: colors.primary,
          color: colors.textPrimary,
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: typography.fontFamily.primary,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {Math.trunc(count)}
      </Box>
    </Tooltip>
  );
}

const EMPTY_HINTS = {
  user_lot: 'Cuando alguien consulte por esta unidad, lo vas a ver acá.',
  cadastral_lot: 'Cuando alguien consulte por este lote, lo vas a ver acá.',
  cad_parcel: 'Cuando alguien consulte por este lote, lo vas a ver acá.',
};

/** "1 consulta · 1 lead" + " · Última actividad: hace 6 d" (solo si hay resumen y fecha válida). */
export function appendLastActivity(summary, lastActivityAt, now) {
  if (!summary) return summary;
  const rel = formatRelativeActivity(lastActivityAt, now);
  return rel ? `${summary} · Última actividad: ${rel}` : summary;
}

/**
 * "Leads y consultas" de un registro (R1, `GET /getrecordleads`).
 * Una sola lista agrupada por lead (nombre + nivel de interés); consultas
 * anidadas; leads solo-interés (`via=interest`) sin hijos.
 *
 * @param {{ itemKind: 'user_lot'|'cadastral_lot'|'cad_parcel', recordId: string, ownerUserId?: string|null,
 *   onOpenLead?: (leadId: string) => void,  refreshKey?: number, lastActivityAt?: string|null }} props
 * `lastActivityAt`: `commercial_summary.last_activity_at` del detalle (dueño / editor). Si viene y hay
 * resumen, se suma "· Última actividad: …" al subtítulo; si no, el subtítulo queda igual.
 */
export default function RecordLeadsSection({
  itemKind = 'user_lot',
  recordId,
  ownerUserId = null,
  onOpenLead = undefined,
  refreshKey = 0,
  lastActivityAt = null,
}) {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState('idle');
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const markingRef = useRef(new Set());
  const abortRef = useRef(null);

  useEffect(() => {
    if (visible) return undefined;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !recordId) return undefined;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setExpandedIds(new Set());
    getRecordLeads({ itemKind, id: recordId, limit: PAGE_SIZE }, { ownerUserId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          if (!isRecordLeadsHidden(res)) console.error('Leads y consultas: getrecordleads falló', res?._httpStatus);
          setState(null);
          setStatus('hidden');
          return;
        }
        setState(readRecordLeads(res));
        setStatus('ready');
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setState(null);
        setStatus('hidden');
      });
    return () => controller.abort();
  }, [visible, itemKind, recordId, ownerUserId, refreshKey]);

  const loadMore = useCallback(async () => {
    if (!state?.hasMore || loadingMore) return;
    setLoadingMore(true);
    const signal = abortRef.current?.signal;
    try {
      const res = await getRecordLeads(
        { itemKind, id: recordId, limit: PAGE_SIZE, before: state.nextBefore, beforeId: state.nextBeforeId },
        { ownerUserId, signal },
      );
      if (signal?.aborted) return;
      if (!res?._httpOk) {
        toast.error('No se pudieron cargar más consultas.');
        return;
      }
      const next = readRecordLeads(res);
      setState((prev) => (prev ? {
        ...prev,
        inquiries: [...prev.inquiries, ...next.inquiries],
        hasMore: next.hasMore,
        nextBefore: next.nextBefore,
        nextBeforeId: next.nextBeforeId,
      } : prev));
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudieron cargar más consultas.');
    } finally {
      setLoadingMore(false);
    }
  }, [state, loadingMore, itemKind, recordId, ownerUserId]);

  const markSeen = useCallback(async (row) => {
    const id = String(row?.id || '');
    if (!id || !isCollectionInquiryUnseen(row) || markingRef.current.has(id)) return;
    markingRef.current.add(id);
    try {
      const res = await updateCollectionInquiry({ id, seen: true });
      if (!res?._httpOk) {
        toast.error(res?.error || 'No se pudo marcar la consulta como vista.');
        return;
      }
      const updated = res.data && typeof res.data === 'object' ? res.data : null;
      setState((prev) => applyRecordInquirySeen(prev, row, updated));
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo marcar la consulta como vista.');
    } finally {
      markingRef.current.delete(id);
    }
  }, []);

  const toggle = (row) => {
    const rowId = String(row?.id || '');
    if (!rowId) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
    markSeen(row);
  };

  const { groups, orphans } = useMemo(
    () => groupInquiriesByLead(state?.inquiries || [], state?.leads || []),
    [state],
  );

  if (status === 'hidden') return null;

  const isEmpty = state && !state.inquiries.length && !state.leads.length;
  const summary = state ? describeRecordLeadsStats(state.stats) : '';
  const unseenTotal = state ? state.stats.unseenCount : 0;
  const headerSubtitle = status === 'ready'
    ? (appendLastActivity(summary, lastActivityAt) || RECORD_LEADS_EMPTY_SUMMARY)
    : 'Cargando…';

  return (
    <Box ref={rootRef} data-testid="record-leads-section" sx={{ ...PRIVATE_BLOCK_SX, p: 2 }}>
      <BlockHeader
        icon={<PersonAddOutlinedIcon sx={{ fontSize: 18, color: '#ABABAB', mt: 0.1 }} />}
        title={RECORD_LEADS_TITLE}
        subtitle={headerSubtitle}
        action={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {state ? <UnseenBadge count={unseenTotal} /> : null}
            <BlockCollapseToggle
              expanded={expanded}
              onToggle={() => setExpanded((v) => !v)}
              labelExpanded="Contraer leads y consultas"
              labelCollapsed="Expandir leads y consultas"
            />
          </Box>
        )}
      />
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {status !== 'ready' ? (
            <>
              <Skeleton variant="rounded" height={52} sx={skSx} />
              <Skeleton variant="rounded" height={52} sx={skSx} />
            </>
          ) : null}

          {status === 'ready' && isEmpty ? (
            <PanelEmptyState
              icon={PersonAddOutlinedIcon}
              title="Sin consultas ni leads"
              hint={EMPTY_HINTS[itemKind] || EMPTY_HINTS.user_lot}
              testId="record-leads-empty"
              sx={{ minHeight: 0, py: 2 }}
            />
          ) : null}

          {status === 'ready' && groups.map((group) => (
            <InquiryLeadGroup
              key={group.leadId}
              group={group}
              expandedIds={expandedIds}
              onToggleInquiry={toggle}
              onMarkSeen={markSeen}
              onOpenLead={onOpenLead}
              showVia
              hideItemLabel
              flatInquiries
            />
          ))}

          {status === 'ready' && orphans.map((row, index) => {
            const rowId = String(row.id || `${row.created_at}-${index}`);
            return (
              <InquiryCard
                key={rowId}
                row={row}
                expanded={expandedIds.has(rowId)}
                onToggle={() => toggle(row)}
                titleMode="visitor"
              />
            );
          })}

          {status === 'ready' && state.hasMore ? (
            <Button
              onClick={loadMore}
              disabled={loadingMore}
              sx={{ alignSelf: 'center', textTransform: 'none', color: colors.textSecondary, fontSize: '12px' }}
            >
              {loadingMore ? 'Cargando…' : 'Ver más consultas'}
            </Button>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
